import React, { ChangeEvent, FormEvent, useState } from 'react';
import { styles } from './TrackMyOrderForm.css';
import TextField from '@jbhi-fi/lanyard-ui/components/TextField/TextField';
import Button from '@jbhi-fi/lanyard-ui/components/Button';

export type TrackMyOrderFormProps = {
  onSubmit: (orderNumber: string, mobileNumber?: string) => void;
  validOrderNumberPrefixes: Array<string>;
  initialOrderNumber?: string;
  initialMobileNumber?: string;
  environment: string;
  isMobileCheckEnabled?: boolean;
  validateMobile: (
    mobileNumber: string,
    countryCode: string
  ) => Promise<{ isMobileValid: boolean; formattedMobile: string; error?: string }>;
};

export const TrackMyOrderForm = ({
  onSubmit,
  validOrderNumberPrefixes,
  initialOrderNumber = '',
  initialMobileNumber = '',
  environment,
  isMobileCheckEnabled,
  validateMobile,
}: TrackMyOrderFormProps) => {
  const [hasOrderInputError, setHasOrderInputError] = useState<boolean>(false);
  const [orderNumber, setOrderNumber] = useState<string>(initialOrderNumber);
  const [mobileNumber, setMobileNumber] = useState<string>(initialMobileNumber);
  const [mobileError, setMobileError] = useState<string | undefined>('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const isOrderNumberValid = validOrderNumberPrefixes.some(prefix => orderNumber.startsWith(prefix));
    setHasOrderInputError(!isOrderNumberValid);

    if (isMobileCheckEnabled) {
      const {
        isMobileValid,
        formattedMobile,
        error: mobileErrorMessage,
      } = await validateMobile(mobileNumber, environment);
      if (!isMobileValid) {
        setMobileError(mobileErrorMessage ?? 'That mobile number does not look quite right.');
        return;
      }
      isOrderNumberValid && onSubmit?.(orderNumber, formattedMobile);
    } else isOrderNumberValid && onSubmit?.(orderNumber);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>, inputType: 'order' | 'mobile') => {
    switch (inputType) {
      case 'order':
        setOrderNumber(e.target.value?.toUpperCase() ?? '');
        setHasOrderInputError(false);
        break;
      case 'mobile':
        setMobileNumber(e.target.value.trim().replace(/[^\d+]/g, ''));
        setMobileError(undefined);
        break;
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        id="input-order-number"
        label="Order number"
        name="orderNumber"
        required={true}
        hasError={hasOrderInputError}
        errorMsg={`That order number does not look quite right. Order numbers start with ${validOrderNumberPrefixes.join(
          ' or '
        )}.`}
        onChange={e => handleChange(e, 'order')}
        value={orderNumber}
      />
      <div className={styles.subText} data-testid="label-find-order">
        You can find this in the top right corner of your confirmation email. e.g. {validOrderNumberPrefixes[0]}123456
      </div>
      {isMobileCheckEnabled ? (
        <>
          <TextField
            id="input-mobile-number"
            label="Mobile number"
            name="mobileNumber"
            required={true}
            hasError={Boolean(mobileError)}
            errorMsg={mobileError}
            onChange={e => handleChange(e, 'mobile')}
            className={styles.mobileInput}
            value={mobileNumber}
            maxLength={15}
          />
          <div className={styles.subText} data-testid="label-find-mobile">
            The number you provided when placing your order
          </div>
        </>
      ) : null}
      <Button
        data-testid="button-track-order"
        submit
        fullWidth
        classes={styles.trackButton}
        type="primary"
        label="Track order"
        onClick={() => null}
      />
    </form>
  );
};
